import { useMutation } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { Base64 } from "js-base64";
import { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { toBlob } from "components/config/helpers";
import ContextConfigFormContentField from "components/ContextConfigForm/ContentField";
import ContextConfigFormFileUploadField from "components/ContextConfigForm/FileUploadField";
import { ENVIRONMENT_QUERY } from "components/Environment/constants";
import {
  AddStackConfigGqlResponse,
  AddStackConfigGqlVariables,
  UpdateStackConfigGql,
  UpdateStackConfigGqlVariables,
} from "components/Environment/edit/types";
import { EnvironmentType } from "components/Environment/types";
import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Tab from "ds/components/Tab";
import TabPanel from "ds/components/TabPanel";
import Textarea from "ds/components/Textarea";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElement, ConfigInput, ConfigType } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { fileToBase64 } from "utils/file";
import { FILE_PREVIEW_MAX_SIZE } from "views/Account/Context/constants";
import { FileMountDrawerTabs } from "views/Account/Context/MountedFiles/ListItem/types";

import { ADD_STACK_CONFIG, UPDATE_STACK_CONFIG } from "../gql";

type DrawerMountedFileProps = {
  file?: ConfigElement;
  stackId: string;
  type: EnvironmentType;
};

type FormValues = {
  id: string;
  description: string;
  fileBody: string;
  file: File | null;
  writeOnly: boolean;
};

const validatePath = (value: string) => {
  if (value.startsWith("/")) {
    return "Mount path must be relative";
  }

  if (value.endsWith("/")) {
    return "Mount path must point to a file";
  }

  return true;
};

const mountedFilesFormDrawer = createDrawer(({ file, stackId, type }: DrawerMountedFileProps) => {
  const isEdit = !!file;
  const drawer = useModal();
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const [currentTab, setCurrentTab] = useState(
    isEdit ? FileMountDrawerTabs.AddManually : FileMountDrawerTabs.Upload
  );
  const blob = useMemo(() => toBlob(file?.value ?? ""), [file?.value]);
  const hasPreviewError = blob.size > FILE_PREVIEW_MAX_SIZE;

  const [addFile, { loading }] = useMutation<
    AddStackConfigGqlResponse,
    Partial<AddStackConfigGqlVariables>
  >(ADD_STACK_CONFIG, {
    refetchQueries: [ENVIRONMENT_QUERY[type]],
    variables: { stackId },
  });

  const [updateFile, { loading: updating }] = useMutation<
    UpdateStackConfigGql,
    UpdateStackConfigGqlVariables
  >(UPDATE_STACK_CONFIG, {
    refetchQueries: [ENVIRONMENT_QUERY[type]],
    variables: { stackId },
  });

  const builderForm = useForm<FormValues>({
    defaultValues: {
      id: file?.id || "",
      description: file?.description || "",
      writeOnly: file?.writeOnly ?? false,
    },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    register,
    setValue,
    watch,
  } = builderForm;

  useEffect(() => {
    const processValue = async () => {
      if (hasPreviewError) {
        return;
      }

      if (blob.size > 0) {
        try {
          setValue("fileBody", await blob.text());
        } catch {
          setValue("fileBody", "");
        }
      } else {
        setValue("fileBody", "");
      }
    };

    processValue();
  }, [blob, hasPreviewError, setValue]);

  const closeDrawer = () => {
    drawer.resolve();
    drawer.hide();
  };

  const isUploadTab = currentTab === FileMountDrawerTabs.Upload;
  const isManuallyAddTab = currentTab === FileMountDrawerTabs.AddManually;

  const onSubmit = async (formData: FormValues) => {
    const promise = isEdit ? updateFile : addFile;
    try {
      const base64 = formData.file && (await fileToBase64(formData.file));
      const base64File = base64 ? base64.toString().split(",")[1] : "";
      const fileValue = formData.file ? base64File : Base64.encode(formData.fileBody);

      const fileData: ConfigInput = {
        id: formData.id,
        description: formData.description || "",
        writeOnly: formData.writeOnly,
        value: fileValue,
        type: ConfigType.FileMount,
        fileMode: null,
      };

      return promise({
        variables: {
          input: fileData,
        },
      }).then(({ data }) => {
        if (data?.stackConfigAdd.id) {
          reportSuccess({
            message: `File ${data?.stackConfigAdd.id} successfully ${isEdit ? "updated" : "added"}`,
          });
          closeDrawer();
        }
      });
    } catch (e) {
      onError(e);
    }
  };

  const content = watch("fileBody");

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...builderForm}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title={isEdit ? "Edit file" : "New file"} />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody gap="x-large" fullHeight>
            <FormField label="Path" error={errors?.id?.message} fullWidth>
              {({ ariaInputProps }) => (
                <Box align="center" gap="small">
                  <Typography variant="p-body3" tag="span">
                    /mnt/workspace/
                  </Typography>

                  <Input
                    placeholder="Enter path here..."
                    error={!!errors.id}
                    disabled={isEdit}
                    {...register("id", {
                      required: "Path field is required.",
                      validate: validatePath,
                    })}
                    {...ariaInputProps}
                  />
                </Box>
              )}
            </FormField>
            <Controller
              name="writeOnly"
              control={control}
              render={({ field }) => (
                <FormToggleField
                  variant="switch"
                  onChange={field.onChange}
                  checked={field.value}
                  title="Mark as secret"
                  description="Secret values are only made available to Runs and Tasks and are not accessible in the web GUI and through the API. Marking variables as a secret is a recommended option."
                />
              )}
            />

            <FormField label="Description" isOptional error={errors?.description?.message}>
              {({ ariaInputProps }) => (
                <Textarea
                  minRows={1}
                  maxRows={15}
                  placeholder="Enter description here..."
                  error={!!errors.description}
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            <FormField label="File content" fullHeight>
              <Box direction="row" gap="medium" fullWidth>
                <Tab
                  onClick={setCurrentTab}
                  isActive={isUploadTab}
                  id={FileMountDrawerTabs.Upload}
                  label="Upload"
                />
                <Tab
                  onClick={setCurrentTab}
                  isActive={isManuallyAddTab}
                  id={FileMountDrawerTabs.AddManually}
                  label="Add manually"
                />
              </Box>

              <TabPanel isActive={isUploadTab} id={FileMountDrawerTabs.Upload} fullHeight>
                <ContextConfigFormFileUploadField name="file" required={!content} />
              </TabPanel>
              <TabPanel isActive={isManuallyAddTab} id={FileMountDrawerTabs.AddManually} fullHeight>
                <ContextConfigFormContentField name="fileBody" />
              </TabPanel>
            </FormField>

            <DrawerFooter>
              <DrawerFooterActions>
                <DrawerCancelButton />
                <Button variant="primary" type="submit" disabled={loading || updating}>
                  {isEdit ? "Save" : "Create"}
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showMountedFilesFormDrawer = createDrawerTrigger(mountedFilesFormDrawer);
